import { Locale } from "date-fns"
import { enUS, es } from "date-fns/locale"
import { Language } from "../common/types"
import i18n from "./i18n"

const DATE_FNS_LOCALES: Record<Language, Locale> = {
    [Language.ENGLISH]: enUS,
    [Language.SPANISH]: es,
}

/**
 * Changes the current language for i18n translations
 * @param language - The language code to change to (e.g. 'en', 'es')
 */
export const changeLanguage = (language: Language): void => {
    i18n.changeLanguage(language)
}

/**
 * Gets the currently active language code
 * @returns The current language code as a Language type
 */
export const getCurrentLanguage = (): Language => {
    return i18n.language as Language
}

/**
 * Gets a list of all available language codes from i18n resources
 * @returns Array of available language codes as Language types
 */
export const getAvailableLanguages = (): Language[] => {
    return Object.keys((i18n.options.resources as Record<Language, any>) || {}) as Language[]
}

/**
 * Gets the date-fns Locale for a given language, for formatting dates (via date-fns' `format`)
 * so they read correctly regardless of the app's own current i18n language -- e.g. the EoD
 * question settings-page preview renders its Spanish tab's date in Spanish even while
 * the surrounding admin UI stays in English.
 * @param language - The language to get a date-fns Locale for
 * @returns The date-fns Locale for that language
 */
export const getDateFnsLocaleForLanguage = (language: Language): Locale => {
    return DATE_FNS_LOCALES[language] ?? enUS
}

/**
 * Gets the date-fns Locale matching the currently active i18n language, for formatting dates
 * (via date-fns' `format`) so they read correctly in the current locale.
 * @returns The date-fns Locale for the current language
 */
export const getCurrentDateFnsLocale = (): Locale => {
    return getDateFnsLocaleForLanguage(getCurrentLanguage())
}
